import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { EquipmentItemCreateData, EquipmentItemData, EquipmentResponse } from "@/types/equipmentTypes";
import { EquipmentItem } from "@/backend/models/equipmentItem";
import { useEffect, useState } from "react";
import { useEquipmentStore } from "@/frontend/store/EquipmentStore";
import { useShallow } from "zustand/react/shallow";
import { Modal, Form, Button } from "react-bootstrap";
import { I18n } from "@/i18n/I18n";
import { INSTRUMENT_CATEGORY } from "@/types/equipmentTypes";
import { InstrumentCategoryDropdown } from "./InstrumentCategoryDropdown";
import { REGEX } from "@/backend/lib/regex";

export function EquipmentItemModal(props: { onConfirm: (item: EquipmentItemCreateData | EquipmentItemData) => Promise<EquipmentResponse>, equipmentItem?: EquipmentItem, setShow: (show: boolean) => void }) {
  const { onConfirm, equipmentItem, setShow } = props;
  const procedureInstanceId = useProcedureInstanceStore((state) => state.procedureInstance?.id) as string;
  const [nameError, setNameError] = useState<string>();
  const [identifierError, setIdentifierError] = useState<string>();
  const [equipmentModel, setEquipmentModel] = useState<EquipmentItemCreateData>({
    name: equipmentItem?.name || '',
    description: equipmentItem?.description || '',
    identifier: equipmentItem?.identifier || '',
    procedureInstanceId,
    category: equipmentItem?.category || INSTRUMENT_CATEGORY.NONE
  });

  const equipmentIdentifiers = useEquipmentStore(useShallow((state) => state.equipmentList.filter((item) => item.id !== equipmentItem?.id).map((item) => item.identifier)));

  useEffect(() => {
    setEquipmentModel({
      name: equipmentItem?.name || '',
      description: equipmentItem?.description || '',
      identifier: equipmentItem?.identifier || '',
      procedureInstanceId,
      category: equipmentItem?.category || INSTRUMENT_CATEGORY.NONE
    });
  }, [equipmentItem, procedureInstanceId]);


  const onChangeInput = (name: string, value: string) => {
    setNameError('');
    setIdentifierError('');

    if (name === 'identifier' && !REGEX.INSTRUMENT_NAME.test(value)) {
      setIdentifierError(I18n.get('equipmentIdentifiersAlphaNumeric'));
    }

    setEquipmentModel({ ...equipmentModel, [name]: value });
  };

  const handleClose = () => {
    setShow(false);
    setNameError('');
    setIdentifierError('');
    setEquipmentModel({ name: '', description: '', identifier: '', procedureInstanceId, category: INSTRUMENT_CATEGORY.NONE });
   };

   const validEquipmentModel = () => {
    let isValid = true;
    if (!equipmentModel.name) {
      setNameError(I18n.get('nameRequired'));
      isValid = false;
    }
    if (!REGEX.INSTRUMENT_NAME.test(equipmentModel.identifier)) {
      setIdentifierError(I18n.get('equipmentIdentifiersAlphaNumeric'));
      isValid = false;
    }
    if (equipmentIdentifiers.includes(equipmentModel.identifier)) {
      setIdentifierError(I18n.get('equipmentIdentifierExists'));
      isValid = false;
    }
    if (!equipmentModel.identifier) {
      setIdentifierError(I18n.get('identifierRequired'));
      isValid = false;
    }
    return isValid;
  };

  const handleSubmission = async () => {
    if (!validEquipmentModel()) return;

    await onConfirm({ ...equipmentModel, id: equipmentItem?.id || '' });
    handleClose();
  };

  return (
    <Modal show onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {equipmentItem ? I18n.get('editModel', { model: I18n.get('equipment'), title: equipmentItem.name }) : I18n.get('createModel', { model: I18n.get('equipment') })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className='d-flex flex-column gap-2'>
          <Form.Group>
            <Form.Label>{I18n.get('title')}</Form.Label>
            <Form.Control
              aria-label={I18n.get('enterField', { field: I18n.get('title') })}
              type="text"
              value={equipmentModel.name}
              onChange={(e) => onChangeInput('name', e.target.value)}
              isInvalid={!!nameError}
            />
            <Form.Control.Feedback type="invalid">
              {nameError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>{I18n.get('identifier')}</Form.Label>
            <Form.Control
              aria-label={I18n.get('enterField', { field: I18n.get('identifier') })}
              type="text"
              value={equipmentModel.identifier}
              onChange={(e) => onChangeInput('identifier', e.target.value)}
              isInvalid={!!identifierError}
            />
            <Form.Control.Feedback type="invalid">
              {identifierError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group >
            <InstrumentCategoryDropdown
              category={equipmentModel.category}
              onChange={onChangeInput}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>{I18n.get('description')} ({I18n.get('optional')})</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={equipmentModel.description}
              onChange={(e) => onChangeInput('description', e.target.value)}
              style={{ resize: 'vertical' }}
              aria-label={I18n.get('enterField', { field: I18n.get('description') })}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" aria-label='close' onClick={handleClose}>
          {I18n.get('cancel')}
        </Button>
        <Button variant="primary" type='submit' aria-label={I18n.get('submit')} onClick={() => void handleSubmission()}>
          {I18n.get('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}